import { Typography } from '@gympass/tai-chi/typography';
import styled from '@mui/material/styles/styled';

import type { SxProps, Theme } from '@mui/material/styles';
import { visuallyHidden as muiVisuallyHidden } from '@mui/utils';

export const springEasing = 'cubic-bezier(.25, 0, .5, 1)';

export const pricingContentStyleOptions = (theme: Theme) =>
  ({
    $alignContent: 'start',
    $paddingBlockStart: theme.spacing(8),
  } as const);

export const PricingSection = styled('section')(({ theme }) => ({
  marginInline: 'auto',
  marginBlockStart: theme.spacing(4),
  '> *:not(:first-child)': {
    marginBlockStart: theme.spacing(6),
  },
}));

export const Paragraph = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  [theme.breakpoints.up('md')]: {
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
  },
}));

const invalidDOMProps: PropertyKey[] = [
  '$actionBarMobileOverride',
  '$align',
  '$alignContent',
  '$aspectRatio',
  '$backgroundColor',
  '$secondaryBackgroundColor',
  '$borderRadius',
  '$bottom',
  '$color',
  '$columns',
  '$disabled',
  '$expanded',
  '$gridFlow',
  '$hasBorder',
  '$hasBackButton',
  '$inlineContentPadding',
  '$isHelperText',
  'isNumber',
  '$isLoading',
  'isLoading',
  '$isSpecialOffer',
  '$left',
  '$maxWidth',
  '$numberOfChildren',
  '$opacity',
  '$paddingBlockStart',
  '$right',
  '$size',
  '$top',
] as const;

/**
 * Checks if a given property key is considered an invalid prop to be passed to a DOM element.
 * This is useful for filtering out custom props from a component's props object before
 * spreading them onto a native HTML element.
 *
 * @param prop - The property key to validate.
 * @returns `true` if the prop is found in the list of invalid DOM props, `false` otherwise.
 */
export const excludeInvalidDOMProps = (prop: PropertyKey) =>
  !invalidDOMProps.includes(prop);

/**
 * A set of CSS properties to visually hide an element while keeping it accessible to screen readers.
 * This is useful for providing accessible labels for elements that are visually represented by icons or other non-textual content.
 *
 * @example
 * <Box sx={visuallyHidden}>This text is hidden but accessible to screen readers.</Box>
 */
export const visuallyHidden: SxProps<Theme> =
  muiVisuallyHidden as SxProps<Theme>;
